import { useState, useEffect } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { LayoutGrid, ShoppingBag, LogOut, User, Settings, Bell, Plus, Pencil, Trash2, Loader2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Badge } from '@/shared/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import { Logo } from '@/shared/components/Logo'
import { cn, notify } from '@/shared/utils'
import { requestNotificationPermission, showBrowserNotification } from '@/shared/utils/browserNotification'
import { useAuth } from '@/features/auth/context/AuthProvider'
import { useVendor } from '@/features/inventory/hooks/useVendor'
import { useInventory } from '@/features/inventory/hooks/useInventory'
import { useCategories } from '@/features/inventory/hooks/useCategories'
import { httpClient } from '@/lib/api/httpClient'
import { ProfileHeader } from '../components/ProfileHeader'
import { ShopStatusToggle } from '../components/ShopStatusToggle'
import { LowStockAlert } from '@/features/inventory/components/LowStockAlert'
import type { AccountMeResponse, Category } from '@/shared/types'

export function DashboardPage() {
  const { vendorId } = useParams<{ vendorId: string }>()
  const location = useLocation()
  const { logout } = useAuth()
  const { vendor, updateCoverImage, isUpdatingCoverImage } = useVendor(vendorId || '')
  const { inventory } = useInventory(vendorId || '')
  const { categories, isLoading: categoriesLoading, createCategory, updateCategory, deleteCategory, isProcessing } =
    useCategories(vendorId || '')

  const [account, setAccount] = useState<AccountMeResponse | null>(null)
  const [newCategory, setNewCategory] = useState('')
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null)
  const [unreadCount, setUnreadCount] = useState(0)
  const [recentNotifications, setRecentNotifications] = useState<string[]>([])
  const [stats, setStats] = useState<{
    totalOrders: number; todayOrders: number; cancelledOrders: number
    deliveredOrders: number; pendingOrders: number
    todayRevenue: number; totalRevenue: number
  } | null>(null)

  useEffect(() => {
    if (!vendorId) return
    const fetchStats = async () => {
      try {
        const res = await httpClient.get(`/vendors/${vendorId}/stats`)
        console.log('[Stats] response:', res.data)
        setStats(res.data?.data)
      } catch (err) {
        console.error('[Stats] fetch error:', err)
      }
    }
    fetchStats()
    const interval = setInterval(fetchStats, 60000)
    return () => clearInterval(interval)
  }, [vendorId])

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const res = await httpClient.get('/vendor/account/me')
        if (res.data?.data?.details) {
          setAccount(res.data.data.details)
        } else if (res.data) {
          setAccount(res.data)
        }
      } catch {
        // Silently handle - account fetch is optional
      }
    }
    fetchAccount()
  }, [])

  const isShopOpen = vendor?.shopState === 'OPEN' || vendor?.shopState === 'KITCHEN_BUSY'

  // Request notification permission
  useEffect(() => {
    requestNotificationPermission()
  }, [])

  useEffect(() => {
    if (!vendorId || !isShopOpen) return
    const poll = async () => {
      try {
        const response = await httpClient.get(`/vendors/${vendorId}/orders/poll`)
        const data = response.data?.data
        if (data?.count > 0) {
          setUnreadCount((prev) => prev + data.count)
          const messages = data.newOrders.map((o: { orderType?: string; orderNumber?: string; id: string }) =>
            `New ${o.orderType?.toLowerCase() || ''} order #${o.orderNumber || o.id.slice(0, 6)}`
          )
          setRecentNotifications((prev) => [...messages, ...prev].slice(0, 20))
          try { new Audio('/notification.mp3').play().catch(() => {}) } catch {}
          notify.success(`${data.count} new order${data.count > 1 ? 's' : ''} received!`)
          showBrowserNotification(
            'New Order Received',
            `${data.count} new order${data.count > 1 ? 's' : ''} received!`
          )
        }
      } catch {}
    }
    const interval = setInterval(poll, 12000)
    return () => clearInterval(interval)
  }, [vendorId, isShopOpen])

  const handleLogout = () => {
    logout()
  }

  const filteredCategories = vendor?.type === undefined ? categories : categories.filter((c) => c.type === vendor.type)
  const sortedCategories = [...filteredCategories].sort((a, b) => a.name.localeCompare(b.name))

  const handleAddCategory = async () => {
    if (!vendor?.type) {
      notify.error('Vendor type not loaded yet')
      return
    }
    const name = newCategory.trim()
    if (!name) {
      notify.error('Enter a category name')
      return
    }

    createCategory({ name, type: vendor.type })
    setNewCategory('')
  }

  const handleUpdateCategory = async () => {
    if (!editingCategoryId) return
    const name = editingName.trim()
    if (!name) {
      notify.error('Enter a category name')
      return
    }

    updateCategory({ categoryId: editingCategoryId, name })
    setEditingCategoryId(null)
    setEditingName('')
  }

  const getCategoryUsageCount = (categoryId: string) => {
    return inventory.filter((item: { categories?: { id: string }[] }) =>
      item.categories?.some((cat) => cat.id === categoryId)
    ).length
  }

  const handleDeleteClick = (category: Category) => {
    const usageCount = getCategoryUsageCount(category.id)
    if (usageCount > 0) {
      setCategoryToDelete(category)
      return
    }
    deleteCategory(category.id)
  }

  const totalItems = inventory.length
  const availableItems =
    vendor?.type === 'FOOD'
      ? inventory.filter((item) => 'isAvailable' in item && item.isAvailable).length
      : inventory.filter((item) => 'variants' in item && item.variants?.some((v) => v.stockQty > 0)).length

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card/90 border-b sticky top-0 z-20 shadow-sm backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Logo href={`/dashboard/${vendorId}`} />
              <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
                <Link
                  to={`/dashboard/${vendorId}`}
                  className={cn(
                    'pb-1 transition-colors border-b-2',
                    location.pathname === `/dashboard/${vendorId}`
                      ? 'text-foreground border-foreground'
                      : 'text-muted-foreground border-transparent hover:text-foreground'
                  )}
                >
                  Dashboard
                </Link>
                <Link
                  to={`/inventory/${vendorId}`}
                  className={cn(
                    'pb-1 transition-colors border-b-2',
                    location.pathname === `/inventory/${vendorId}`
                      ? 'text-foreground border-foreground'
                      : 'text-muted-foreground border-transparent hover:text-foreground'
                  )}
                >
                  Inventory
                </Link>
                <Link
                  to={`/orders/${vendorId}`}
                  className={cn(
                    'pb-1 transition-colors border-b-2',
                    location.pathname === `/orders/${vendorId}`
                      ? 'text-foreground border-foreground'
                      : 'text-muted-foreground border-transparent hover:text-foreground'
                  )}
                >
                  Orders
                </Link>
              </nav>
            </div>

            <div className="flex items-center gap-4">
              <DropdownMenu onOpenChange={(open) => { if (open) setUnreadCount(0) }}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative">
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {recentNotifications.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-muted-foreground">No new notifications.</div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto">
                      {recentNotifications.map((notif, i) => (
                        <DropdownMenuItem key={i} className="text-sm py-2">
                          {notif}
                        </DropdownMenuItem>
                      ))}
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{account?.name || 'Account'}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{account?.name}</p>
                      <p className="text-xs text-muted-foreground">{account?.phone}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={`/dashboard/${vendorId}/profile`} className="cursor-pointer">
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Welcome back, {account?.name || 'Vendor'}!</h1>
          <p className="text-muted-foreground">
            Here's what's happening with your {vendor?.type?.toLowerCase() || 'business'} today.
          </p>
        </div>

        {vendorId && vendor?.type && (
          <div className="mb-6">
            <ShopStatusToggle vendorId={vendorId} vendorType={vendor.type} shopState={vendor.shopState} />
          </div>
        )}

        {/* Revenue banner */}
        <div className="rounded-xl border bg-card p-5 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Today's Earnings</p>
              <p className="text-3xl font-bold text-foreground">₹{(stats?.todayRevenue || 0).toLocaleString('en-IN')}</p>
              <p className="text-xs text-muted-foreground mt-1">{stats?.todayOrders || 0} orders today</p>
            </div>
            <div className="h-px sm:h-12 sm:w-px bg-border" />
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Earnings</p>
              <p className="text-3xl font-bold text-foreground">₹{(stats?.totalRevenue || 0).toLocaleString('en-IN')}</p>
              <p className="text-xs text-muted-foreground mt-1">{stats?.deliveredOrders || 0} orders delivered</p>
            </div>
          </div>
        </div>

        {/* Stat grid */}
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          <MiniStat label="Total Orders" value={stats?.totalOrders || 0} />
          <MiniStat label="Today" value={stats?.todayOrders || 0} />
          <MiniStat label="Pending" value={stats?.pendingOrders || 0} accent={!!stats?.pendingOrders} />
          <MiniStat label="Cancelled" value={stats?.cancelledOrders || 0} />
          <MiniStat label="Total Items" value={totalItems} />
          <MiniStat label="Available" value={availableItems} />
        </div>

        {/* Quick Actions — moved up for mobile */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-3">Quick Actions</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <ActionCard href={`/inventory/${vendorId}`} title="Manage Inventory" description="View, add, and edit your products" icon={LayoutGrid} />
            <ActionCard href={`/orders/${vendorId}`} title="View Orders" description="Manage and track customer orders" icon={ShoppingBag} />
            <ActionCard href="#" title="Settings" description="Configure your vendor account" icon={Settings} comingSoon />
          </div>
        </div>

        {vendor?.type === 'CLOTHING' && vendorId && (
          <LowStockAlert vendorId={vendorId} />
        )}

        {/* Vendor Profile Header */}
        <ProfileHeader
          vendor={vendor ?? null}
          inventory={inventory}
          onCoverImageUpload={updateCoverImage}
          isUploadingCover={isUpdatingCoverImage}
        />

        <div className="mt-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Categories</h2>
              <p className="text-sm text-muted-foreground">Organize your catalog and attach items to multiple categories.</p>
            </div>
            <div className="flex w-full md:w-auto gap-2">
              <Input
                placeholder="Add a new category"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddCategory()
                  }
                }}
                disabled={isProcessing}
              />
              <Button onClick={handleAddCategory} disabled={isProcessing}>
                {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" />Add</>}
              </Button>
            </div>
          </div>
          <div className="bg-card border rounded-xl p-4">
            {categoriesLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Loading categories...</span>
              </div>
            ) : sortedCategories.length === 0 ? (
              <p className="text-sm text-muted-foreground">No categories yet. Create one to get started.</p>
            ) : (
              <div className="divide-y">
                {sortedCategories.map((category) => (
                  <div key={category.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{category.type}</Badge>
                      {editingCategoryId === category.id ? (
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="w-56"
                          autoFocus
                        />
                      ) : (
                        <p className="font-medium text-foreground">{category.name}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {editingCategoryId === category.id ? (
                        <>
                          <Button size="sm" onClick={handleUpdateCategory} disabled={isProcessing}>Save</Button>
                          <Button variant="ghost" size="sm" onClick={() => { setEditingCategoryId(null); setEditingName('') }} disabled={isProcessing}>Cancel</Button>
                        </>
                      ) : (
                        <>
                          <Button variant="outline" size="sm" onClick={() => { setEditingCategoryId(category.id); setEditingName(category.name) }} disabled={isProcessing}>
                            <Pencil className="w-4 h-4 mr-2" />Edit
                          </Button>
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDeleteClick(category)} disabled={isProcessing}>
                            <Trash2 className="w-4 h-4 mr-2" />Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <AlertDialog open={!!categoryToDelete} onOpenChange={(open) => { if (!open) setCategoryToDelete(null) }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete category "{categoryToDelete?.name}"?</AlertDialogTitle>
              <AlertDialogDescription>
                This category is assigned to {categoryToDelete ? getCategoryUsageCount(categoryToDelete.id) : 0} item(s). Deleting it will remove the category from those items.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setCategoryToDelete(null)} disabled={isProcessing}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => { if (categoryToDelete) { deleteCategory(categoryToDelete.id); setCategoryToDelete(null) } }} disabled={isProcessing} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}

function MiniStat({ label, value, accent = false }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className={cn('rounded-lg border p-3 text-center', accent ? 'border-primary/30 bg-primary/5' : 'bg-card')}>
      <p className={cn('text-2xl font-bold', accent ? 'text-primary' : 'text-foreground')}>{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </div>
  )
}

interface ActionCardProps {
  href: string
  title: string
  description: string
  icon: React.ElementType
  comingSoon?: boolean
}

function ActionCard({ href, title, description, icon: Icon, comingSoon = false }: ActionCardProps) {
  if (comingSoon) {
    return (
      <div className="relative bg-card rounded-xl border p-6 opacity-60 cursor-not-allowed">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-primary/10 text-primary">
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-1">{title}</h3>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
        <div className="absolute top-3 right-3">
          <span className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded-full">Coming Soon</span>
        </div>
      </div>
    )
  }

  return (
    <Link to={href} className="flex items-center gap-4 p-6 bg-card rounded-xl border transition-all duration-200 hover:shadow-md hover:border-primary/30 text-left group">
      <div className="p-3 rounded-lg bg-primary/10 text-primary transition-colors">
        <Icon className="w-6 h-6" />
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </Link>
  )
}
